/**
 * SOLID Review
 * S — Strong. One concern: the re-acceptance gate's flow — asking whether the signed-in player must re-accept, the documents to
 *     show, the checkbox, and recording the acceptance. The rules — engage only on an explicit "not all current", Terms then Privacy —
 *     are the Domain's (`domain/legal/LegalReview.ts`).
 * O — Good. A new document type is the Domain's list; this shows what it is handed.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The checkbox and the acceptance, and the view's one input.
 * D — Strong. Two use cases and the request contexts.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { ClientRequests } from '../client/ClientRequests';
import { messageFor } from '../client/failureMessage';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

type TReview = TClientUseCaseInput<'acceptLegalTerms'>['review'];

const CHECK: TClientUseCaseInput<'checkLegalReview'> = {};

export interface IStateConsentReacceptance {
  readonly reviewDocs: TReview;
  readonly checked: boolean;
  readonly submitting: boolean;
  readonly error: string | null;
}

const SUBMITTING: Partial<IStateConsentReacceptance> = { submitting: true, error: null };
const SUBMITTED: Partial<IStateConsentReacceptance> = { submitting: false };

/** What the gate's view hands it: whether a signed-in registered player is seated. */
type TGateInputs = readonly [active: boolean];

/**
 * THE RE-ACCEPTANCE GATE (frontend-framework-oo P3, 2026-10-29, §28.295) — `legal/components/ConsentReacceptanceGate.tsx`'s state,
 * its effect and its handler. While active it asks once whether the player's recorded consent still matches the current Privacy
 * Policy and Terms (an answer that comes back after the gate went quiet is dropped); the documents to re-accept block the screen
 * until accepted. Fails open: any error asking leaves the app usable — the use case answers "nothing to accept".
 */
export class ConsentReacceptanceViewModel implements IServiceViewModel<IStateConsentReacceptance> {
  private readonly state: ObservableState<IStateConsentReacceptance>;
  private readonly reactions = new Reactions<TGateInputs>([{
    deps: (inputs) => inputs,
    run: ([active]) => {
      if (!active) return undefined;
      let cancelled = false;
      void this.useCases.checkLegalReview(this.requests.context(), CHECK).then((checkedReview) => {
        if (cancelled || !checkedReview.success) return;
        const toReview: Partial<IStateConsentReacceptance> = { reviewDocs: checkedReview.value.review };
        this.state.update(toReview);
      });
      return () => {
        cancelled = true;
      };
    },
  }]);

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'checkLegalReview' | 'acceptLegalTerms'>,
    private readonly requests: ClientRequests,
  ) {
    // No document to re-accept — the gate renders nothing.
    const quiet: IStateConsentReacceptance = { reviewDocs: [], checked: false, submitting: false, error: null };
    this.state = new ObservableState<IStateConsentReacceptance>(quiet);
  }

  readonly setChecked = (checked: boolean): void => {
    const ticked: Partial<IStateConsentReacceptance> = { checked };
    this.state.update(ticked);
  };

  readonly accept = async (): Promise<void> => {
    const { checked, submitting, reviewDocs } = this.state.get();
    /* c8 ignore next -- redundant with the accept button's disabled={!checked || submitting}; kept as a defensive guard against
       programmatic invocation */
    if (!checked || submitting) return;
    this.state.update(SUBMITTING);
    try {
      const command: TClientUseCaseInput<'acceptLegalTerms'> = { review: reviewDocs };
      const accepted = await this.useCases.acceptLegalTerms(this.requests.context(), command);
      if (!accepted.success) {
        const failed: Partial<IStateConsentReacceptance> = { error: messageFor(accepted.error, 'Could not record your acceptance. Please try again.') };
        this.state.update(failed);
      } else {
        const accepted: Partial<IStateConsentReacceptance> = { reviewDocs: [] };
        this.state.update(accepted);
      }
    } finally {
      this.state.update(SUBMITTED);
    }
  };

  getSnapshot(): IStateConsentReacceptance {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TGateInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TGateInputs);
  }
}
